import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  useNCR,
  useNCRHistory,
  useChangeNCRStatus,
  useUpdateNCR,
  useAddNCRComment,
  getStatusLabel,
  getStatusColor,
  getSeverityLabel,
  getSeverityColor,
  getTypeLabel,
  getNextStatus,
  getActionLabel,
  ROOT_CAUSE_METHODS,
  NCRStatus,
} from '@/hooks/useNCR';
import {
  AlertTriangle,
  Calendar,
  User,
  Clock,
  ArrowRight,
  MessageSquare,
  FileText,
  CheckCircle,
  XCircle,
  Loader2,
  Send,
  ExternalLink,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

interface NCRDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ncrId: string | null;
  onEdit?: () => void;
}

export function NCRDetailsDialog({
  open,
  onOpenChange,
  ncrId,
  onEdit,
}: NCRDetailsDialogProps) {
  const navigate = useNavigate();
  const { data: ncr, isLoading } = useNCR(ncrId);
  const { data: history } = useNCRHistory(ncrId);
  const changeStatus = useChangeNCRStatus();
  const updateNCR = useUpdateNCR();
  const addComment = useAddNCRComment();

  const [comment, setComment] = useState('');
  const [rootCause, setRootCause] = useState('');
  const [correctiveAction, setCorrectiveAction] = useState('');
  const [verificationResult, setVerificationResult] = useState('');

  if (!ncrId) return null;

  const handleAdvanceStatus = async () => {
    if (!ncr) return;
    const nextStatus = getNextStatus(ncr.status);
    if (nextStatus) {
      await changeStatus.mutateAsync({
        ncrId: ncr.id,
        newStatus: nextStatus,
        comment: `Avanzando a: ${getStatusLabel(nextStatus)}`,
      });
    }
  };

  const handleAddComment = async () => {
    if (!ncr || !comment.trim()) return;
    await addComment.mutateAsync({ ncrId: ncr.id, comment: comment.trim() });
    setComment('');
  };

  const handleSaveRootCause = async () => {
    if (!ncr || !rootCause.trim()) return;
    await updateNCR.mutateAsync({
      id: ncr.id,
      data: { root_cause: rootCause.trim() },
    });
  };

  const handleSaveCorrectiveAction = async () => {
    if (!ncr || !correctiveAction.trim()) return;
    await updateNCR.mutateAsync({
      id: ncr.id,
      data: { corrective_action: correctiveAction.trim() },
    });
  };

  const handleCloseNCR = async () => {
    if (!ncr) return;
    await changeStatus.mutateAsync({
      ncrId: ncr.id,
      newStatus: 'closed',
      comment: verificationResult || 'NCR cerrada',
    });
    await updateNCR.mutateAsync({
      id: ncr.id,
      data: {
        verification_result: verificationResult,
        effectiveness_verified: true,
      },
    });
  };

  const STATUS_STEPS: NCRStatus[] = ['open', 'investigation', 'corrective_action', 'verification', 'closed'];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              {ncr?.ncr_number || 'Cargando...'}
            </div>
            {ncr && (
              <div className="flex items-center gap-2">
                <Badge className={getSeverityColor(ncr.severity)}>
                  {getSeverityLabel(ncr.severity)}
                </Badge>
                <Badge variant="outline" className={getStatusColor(ncr.status)}>
                  {getStatusLabel(ncr.status)}
                </Badge>
              </div>
            )}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : ncr ? (
          <ScrollArea className="flex-1">
            <div className="space-y-6 pr-4">
              {/* Status Progress */}
              <div className="flex items-center justify-between px-4 py-3 bg-muted/50 rounded-lg">
                {STATUS_STEPS.map((step, index) => {
                  const isActive = ncr.status === step;
                  const isPast = STATUS_STEPS.indexOf(ncr.status) > index ||
                    (ncr.status === 'closed' && step !== 'closed') ||
                    (ncr.status === 'cancelled');
                  const isCancelled = ncr.status === 'cancelled';

                  return (
                    <div key={step} className="flex items-center">
                      <div
                        className={cn(
                          'flex items-center justify-center w-8 h-8 rounded-full text-xs font-medium',
                          isActive && 'bg-primary text-primary-foreground',
                          isPast && !isCancelled && 'bg-green-500 text-white',
                          !isActive && !isPast && 'bg-muted text-muted-foreground',
                          isCancelled && 'bg-gray-400 text-white'
                        )}
                      >
                        {isPast && !isCancelled ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : isCancelled ? (
                          <XCircle className="w-4 h-4" />
                        ) : (
                          index + 1
                        )}
                      </div>
                      <span className={cn(
                        'ml-2 text-xs hidden sm:block',
                        isActive && 'font-medium',
                        !isActive && !isPast && 'text-muted-foreground'
                      )}>
                        {getStatusLabel(step)}
                      </span>
                      {index < STATUS_STEPS.length - 1 && (
                        <ArrowRight className="w-4 h-4 mx-2 text-muted-foreground" />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Header Info */}
              <div>
                <h2 className="text-lg font-semibold">{ncr.title}</h2>
                <div className="flex flex-wrap gap-4 mt-2 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {format(new Date(ncr.opened_at), "d MMM yyyy", { locale: es })}
                  </span>
                  <span className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    {ncr.reporter_name || 'Sin nombre'}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {ncr.days_open} días abierta
                  </span>
                  {ncr.is_overdue && (
                    <Badge variant="destructive">Vencida</Badge>
                  )}
                </div>
              </div>

              <Tabs defaultValue="details" className="w-full">
                <TabsList>
                  <TabsTrigger value="details">Detalles</TabsTrigger>
                  <TabsTrigger value="analysis">Análisis</TabsTrigger>
                  <TabsTrigger value="actions">Acciones</TabsTrigger>
                  <TabsTrigger value="history">Historial</TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="space-y-4 mt-4">
                  <Card>
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm">Descripción del Problema</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <p className="text-sm whitespace-pre-wrap">{ncr.description}</p>
                    </CardContent>
                  </Card>

                  <div className="grid grid-cols-2 gap-4">
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-sm space-y-2">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Tipo:</span>
                            <span>{getTypeLabel(ncr.ncr_type)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Categoría:</span>
                            <span>{ncr.category || '-'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Asignado a:</span>
                            <span>{ncr.assignee_name || 'Sin asignar'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Fecha límite:</span>
                            <span>{ncr.due_date ? format(new Date(ncr.due_date), 'dd/MM/yyyy') : '-'}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-sm space-y-2">
                          {ncr.work_order_number && (
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground">Orden:</span>
                              <Button
                                variant="link"
                                size="sm"
                                className="h-auto p-0"
                                onClick={() => navigate(`/work-orders?open=${ncr.work_order_id}`)}
                              >
                                {ncr.work_order_number}
                                <ExternalLink className="w-3 h-3 ml-1" />
                              </Button>
                            </div>
                          )}
                          {ncr.machine_name && (
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Máquina:</span>
                              <span>{ncr.machine_name}</span>
                            </div>
                          )}
                          {ncr.mold_name && (
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Molde:</span>
                              <span>{ncr.mold_name}</span>
                            </div>
                          )}
                          {ncr.customer_name && (
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Cliente:</span>
                              <span>{ncr.customer_name}</span>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {ncr.immediate_action && (
                    <Card>
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm">Acción Inmediata (Contención)</CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <p className="text-sm whitespace-pre-wrap">{ncr.immediate_action}</p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                <TabsContent value="analysis" className="space-y-4 mt-4">
                  <Card>
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm">Análisis de Causa Raíz</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 space-y-3">
                      {ncr.root_cause ? (
                        <p className="text-sm whitespace-pre-wrap">{ncr.root_cause}</p>
                      ) : ncr.status === 'investigation' ? (
                        <div className="space-y-2">
                          <Textarea
                            placeholder="Describa la causa raíz identificada..."
                            value={rootCause}
                            onChange={(e) => setRootCause(e.target.value)}
                            rows={4}
                          />
                          <Button
                            size="sm"
                            onClick={handleSaveRootCause}
                            disabled={!rootCause.trim() || updateNCR.isPending}
                          >
                            {updateNCR.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Guardar Análisis
                          </Button>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">Sin análisis registrado</p>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="actions" className="space-y-4 mt-4">
                  <Card>
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm">Acción Correctiva</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 space-y-3">
                      {ncr.corrective_action ? (
                        <p className="text-sm whitespace-pre-wrap">{ncr.corrective_action}</p>
                      ) : ncr.status === 'corrective_action' ? (
                        <div className="space-y-2">
                          <Textarea
                            placeholder="Describa la acción correctiva implementada..."
                            value={correctiveAction}
                            onChange={(e) => setCorrectiveAction(e.target.value)}
                            rows={4}
                          />
                          <Button
                            size="sm"
                            onClick={handleSaveCorrectiveAction}
                            disabled={!correctiveAction.trim() || updateNCR.isPending}
                          >
                            {updateNCR.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Guardar Acción
                          </Button>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">Sin acción correctiva registrada</p>
                      )}
                    </CardContent>
                  </Card>

                  {ncr.status === 'verification' && (
                    <Card>
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm">Verificación de Efectividad</CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0 space-y-3">
                        <Textarea
                          placeholder="Describa el resultado de la verificación..."
                          value={verificationResult}
                          onChange={(e) => setVerificationResult(e.target.value)}
                          rows={3}
                        />
                        <Button
                          onClick={handleCloseNCR}
                          disabled={changeStatus.isPending}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          {changeStatus.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Cerrar NCR
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                <TabsContent value="history" className="mt-4">
                  <Card>
                    <CardContent className="pt-4">
                      <div className="space-y-4">
                        {/* Add comment */}
                        <div className="flex gap-2">
                          <Textarea
                            placeholder="Agregar comentario..."
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            rows={2}
                            className="flex-1"
                          />
                          <Button
                            size="icon"
                            onClick={handleAddComment}
                            disabled={!comment.trim() || addComment.isPending}
                          >
                            {addComment.isPending ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4" />
                            )}
                          </Button>
                        </div>

                        <Separator />

                        {/* History list */}
                        <div className="space-y-3">
                          {history?.map((item) => (
                            <div key={item.id} className="flex gap-3 text-sm">
                              <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                                {item.action === 'comment' ? (
                                  <MessageSquare className="w-4 h-4" />
                                ) : item.action === 'status_change' ? (
                                  <ArrowRight className="w-4 h-4" />
                                ) : (
                                  <FileText className="w-4 h-4" />
                                )}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{item.user_name}</span>
                                  <span className="text-muted-foreground">
                                    {getActionLabel(item.action)}
                                  </span>
                                  {item.new_status && (
                                    <Badge variant="outline" className="text-xs">
                                      {getStatusLabel(item.new_status)}
                                    </Badge>
                                  )}
                                </div>
                                {item.comment && (
                                  <p className="text-muted-foreground mt-1">{item.comment}</p>
                                )}
                                <span className="text-xs text-muted-foreground">
                                  {formatDistanceToNow(new Date(item.created_at), {
                                    addSuffix: true,
                                    locale: es,
                                  })}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>

              {/* Actions */}
              {ncr.status !== 'closed' && ncr.status !== 'cancelled' && (
                <div className="flex justify-between pt-4 border-t">
                  <Button variant="outline" onClick={onEdit}>
                    Editar NCR
                  </Button>
                  <div className="flex gap-2">
                    {getNextStatus(ncr.status) && (
                      <Button
                        onClick={handleAdvanceStatus}
                        disabled={changeStatus.isPending}
                      >
                        {changeStatus.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        Avanzar a {getStatusLabel(getNextStatus(ncr.status)!)}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            NCR no encontrada
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default NCRDetailsDialog;
